import { MouseEvent } from "react";

const DEFAULT_COMPANY_NAME = "코어데브";
const DEFAULT_CONTACT_NAME = "원현우";
const DEFAULT_CONTACT_TEL = "010-4681-1565";
const DEFAULT_CONTACT_EMAIL = "sajutalktalk@gmail.com";
const EMPTY_INFO_PLACEHOLDER = "";

interface FortuneLabPrivacyPolicyModalProps {
  open: boolean;
  onClose: () => void;
  companyName?: string | null;
  contactName?: string | null;
  contactTel?: string | null;
  contactEmail?: string | null;
  useDefaultFallback?: boolean;
}

const FortuneLabPrivacyPolicyModal = ({
  open,
  onClose,
  companyName,
  contactName,
  contactTel,
  contactEmail,
  useDefaultFallback = false,
}: FortuneLabPrivacyPolicyModalProps) => {
  if (!open) {
    return null;
  }

  const trimmedCompanyName = companyName?.trim() || "";
  const trimmedContactName = contactName?.trim() || "";
  const trimmedContactTel = contactTel?.trim() || "";
  const trimmedContactEmail = contactEmail?.trim() || "";

  const resolvedCompanyName = useDefaultFallback
    ? trimmedCompanyName || DEFAULT_COMPANY_NAME
    : trimmedCompanyName || EMPTY_INFO_PLACEHOLDER;
  const resolvedContactName = useDefaultFallback
    ? trimmedContactName || DEFAULT_CONTACT_NAME
    : trimmedContactName || EMPTY_INFO_PLACEHOLDER;

  const resolvedContactTel = useDefaultFallback
    ? trimmedContactTel || DEFAULT_CONTACT_TEL
    : trimmedContactTel;
  const resolvedContactEmail = useDefaultFallback
    ? trimmedContactEmail || DEFAULT_CONTACT_EMAIL
    : trimmedContactEmail;
  const contactInfo = [resolvedContactTel, resolvedContactEmail].filter((value) => value).join(" / ");
  const resolvedContactLine = contactInfo || EMPTY_INFO_PLACEHOLDER;

  const handleRootClick = () => {
    onClose();
  };

  const stopPropagation = (event: MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
  };

  return (
    <div className="modal_backdrop" onClick={handleRootClick}>
      <div className="modal_container privacy_policy_modal" onClick={stopPropagation}>
        <div className="modal_header privacy">
          <div className="modal_title">개인정보 처리방침</div>
          <button type="button" className="modal_close_button" onClick={onClose} aria-label="닫기">
            &times;
          </button>
        </div>
        <div className="privacy_modal_body">
          <h2>개인정보 수집·이용 동의서</h2>
          <p>
            <strong>{resolvedCompanyName}</strong> (이하 "회사")는 고객님의 개인정보를 중요하게 생각하며, 개인정보보호 관련 법령을
            준수하여 개인정보를 안전하게 관리하고 처리하기 위해 최선을 다하고 있습니다. 본 동의서는 고객님의 개인정보 수집 및
            이용과 관련된 내용을 안내하며, 이를 통해 고객님께 최적의 서비스를 제공하고자 합니다.
          </p>

          <section className="privacy_modal_section">
            <h3>1. 수집하는 개인정보 항목 및 수집 방법</h3>
            <p>회사는 서비스 이용을 위해 아래의 개인정보를 수집합니다.</p>
            <h4>필수 수집 정보</h4>
            <ul>
              <li>이름</li>
              <li>전화번호</li>
              <li>생년월일 (사주 분석을 위한 정보)</li>
            </ul>
            <h4>자동 수집 정보</h4>
            <ul>
              <li>IP 주소</li>
              <li>쿠키</li>
              <li>접속 기록</li>
              <li>기기 정보</li>
            </ul>
          </section>

          <section className="privacy_modal_section">
            <h3>2. 개인정보의 수집 및 이용 목적</h3>
            <p>회사는 수집된 개인정보를 아래와 같은 목적으로 사용합니다.</p>
            <h4>1) 서비스 제공 및 관리</h4>
            <ul>
              <li>개인 맞춤형 사주 분석 결과 제공</li>
              <li>생년월일 및 관련 정보를 활용한 오늘의 운세 서비스 제공</li>
              <li>고객 요청 및 문의 사항 응대</li>
            </ul>
            <h4>2) 마케팅 및 프로모션 활용</h4>
            <ul>
              <li>맞춤형 이벤트 및 프로모션 안내</li>
              <li>고객별 관심사 기반의 서비스 추천</li>
              <li>SMS를 통한 광고 및 정보성 알림 발송</li>
              <li>알림톡을 통한 쿠폰 안내 및 소멸에 대해서 정기적으로 안내합니다</li>
              <li>유효기간이 지난 쿠폰은 소멸되며 사용이 불가합니다</li>
            </ul>
            <h4>3) 통계 및 서비스 품질 개선</h4>
            <ul>
              <li>서비스 이용 기록 분석을 통한 콘텐츠 최적화</li>
              <li>신규 서비스 개발 및 기존 서비스 개선</li>
            </ul>
            <h4>4) 법적 의무 준수 및 분쟁 대응</h4>
            <ul>
              <li>관련 법령에 따라 개인정보 보관 및 제공</li>
            </ul>
          </section>

          <section className="privacy_modal_section">
            <h3>3. 개인정보의 보유 및 이용 기간</h3>
            <p>
              회사는 원칙적으로 개인정보의 수집 및 이용 목적이 달성된 후에는 해당 정보를 지체 없이 파기합니다. 다만, 아래의 경우에는
              명시된 기간 동안 정보를 보관합니다.
            </p>
            <h4>1) 서비스 이용 기록</h4>
            <ul>
              <li>서비스 품질 개선 및 고객 관리 목적으로 수집일로부터 <strong>1년간 보관</strong></li>
            </ul>
            <h4>2) 관련 법령에 따른 보관</h4>
            <ul>
              <li>계약 또는 청약철회 기록: 5년 (전자상거래법)</li>
              <li>소비자 불만 또는 분쟁 처리 기록: 3년 (전자상거래법)</li>
              <li>접속 기록: 6개월 (통신비밀보호법)</li>
            </ul>
            <p>보유 기간 경과 후에는 관련 법령에 따라 안전하게 삭제 처리합니다.</p>
          </section>

          <section className="privacy_modal_section">
            <h3>4. 개인정보의 제3자 제공</h3>
            <p>회사는 고객님의 동의 없이 개인정보를 제3자에게 제공하지 않습니다. 다만, 아래의 경우에 한하여 개인정보를 제공합니다.</p>
            <ul>
              <li>개인정보를 제공받는 자: {resolvedCompanyName}</li>
              <li>제공 목적: 사주 분석 및 운세 결과 발송</li>
              <li>제공하는 항목: 이름, 생년월일, 연락처</li>
              <li>보유 및 이용 기간: 분석 후 3년간 보관</li>
            </ul>
            <p>
              고객님은 제3자 제공에 동의하지 않을 권리가 있으며, 동의 거부 시 일부 서비스 이용이 제한될 수 있습니다.
            </p>
          </section>

          <section className="privacy_modal_section">
            <h3>5. 개인정보 처리 위탁</h3>
            <p>회사는 원활한 서비스 제공을 위해 개인정보 처리 업무를 신뢰할 수 있는 외부 업체에 위탁할 수 있습니다.</p>
            <p>위탁 업무와 수탁 업체는 아래와 같습니다.</p>
            <h4>수탁업체명: 비즈엠</h4>
            <ul>
              <li>위탁업무: 카카오톡 알림톡 발송 및 쿠폰 발급과 소멸 정보성메시지 알림톡 발송업무</li>
            </ul>
            <p>회사는 수탁 업체가 개인정보를 안전하게 처리할 수 있도록 관리·감독합니다.</p>
          </section>

          <section className="privacy_modal_section">
            <h3>6. 개인정보보호 책임자</h3>
            <ul>
              <li>이름: {resolvedContactName}</li>
              <li>연락처: {resolvedContactLine}</li>
            </ul>
          </section>

          <section className="privacy_modal_section">
            <h3>정책 변경</h3>
            <p>
              본 개인정보처리방침은 법령 또는 회사 내부 정책에 따라 변경될 수 있습니다. 변경 사항은 회사 웹사이트를 통해 사전에
              공지됩니다.
            </p>
          </section>
          <button type="button" className="privacy_modal_cta_button" onClick={onClose}>
            운세 보러가기
          </button>
        </div>
      </div>
    </div>
  );
};

export default FortuneLabPrivacyPolicyModal;
